import { getDeviceId, getUserId } from '@amplitude/analytics-browser';

import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';
import { isLocalStorageAvailable } from 'wanderu-utils';

const logger = Logger.getLogger('clientApiService');

let cachedAuthToken;
let cachedSessionId;
let cachedAuthCredentials = {
    accessToken: undefined, // federated logins only
    email: undefined, // cognito only,
    provider: undefined,
    timestamp: undefined,
};

let onSessionChangeListener = () => undefined;
// Federated (google/facebook) re-auth seam (R3). The account layer registers an
// async callback that mints a fresh WAPI session via the provider SDK + auth.json
// and owns clear-on-failure. clientApiService stays ignorant of the SDK: it only
// awaits this opaque callback on a federated 401, then retries the original
// request. Never re-POSTs the stale cached provider token.
let reauthenticate = null;
let authOperation = null;
// Coalesces concurrent federated 401s onto a single re-auth. authOperation only
// de-dupes startNewSession/refreshSession (auth.json/refresh.json); it does NOT
// cover the injected reauthenticate() path (Google SDK + a direct auth.json
// POST). Without this guard, parallel 401s each fire their own SDK prompt, and a
// late definitive failure could clear the session an early success established.
let reauthOperation = null;
let affiliateId: string | undefined;

interface InitServiceParams {
    affiliateId?: string;
}

function initService({ affiliateId: passedAffiliateId }: InitServiceParams) {
    affiliateId = passedAffiliateId;
}

function setUserAuthTokens(authTokens) {
    if (!isLocalStorageAvailable()) {
        return;
    }

    window.localStorage.setItem('userAccessToken', JSON.stringify(authTokens));
}

function isSameAuthCreds(newAuthCreds, oldAuthCreds) {
    if (newAuthCreds.provider !== oldAuthCreds.provider) {
        return false;
    }

    return newAuthCreds.provider === 'cognito'
        ? newAuthCreds.email === oldAuthCreds.email
        : newAuthCreds.accessToken === oldAuthCreds.accessToken;
}

function restoreSession(sessionInfo) {
    const timestamp = Date.now();
    setSession({ ...sessionInfo, timestamp });
}

function setAuthCredentials({
    provider, accessToken, email, timestamp,
}) {
    cachedAuthCredentials = {
        accessToken,
        email,
        provider,
        timestamp,
    };
}

function setSession(sessionInfo) {
    const {
        accessToken,
        authToken,
        email,
        provider,
        sessionId,
        timestamp,
    } = sessionInfo;
    const {
        timestamp: prevTimestamp,
    } = cachedAuthCredentials;

    if (timestamp < prevTimestamp) {
        return;
    }

    setAuthCredentials({
        accessToken,
        email,
        provider,
        timestamp,
    });

    cachedAuthToken = authToken;
    cachedSessionId = sessionId;

    onSessionChangeListener(sessionId);
}

/* cognito provider only */
async function refreshSession(email, expiredAuthToken, preserveSessionId = false) {
    try {
        const payload = {
            email,
            meta: getSessionMeta(),
            sessionId: preserveSessionId
                ? cachedSessionId
                : undefined,
            token: expiredAuthToken,
        };

        const url = buildWapiUrl('refresh.json');

        authOperation = fetch(url, {
            body: JSON.stringify(payload),
            headers: getHeaders('', 'text/plain'),
            method: 'post',
        });

        const response = await authOperation;
        const json = await response.json();

        setSession({
            accessToken: undefined,
            authToken: json.result.token,
            email,
            provider: 'cognito',
            sessionId: json.result.sessionId,
        });

        // overwrite existing user account info in localstorage
        setUserAuthTokens({
            accessTokenExp: json.result.expDt,
            authToken: json.result.token,
            email,
            identityId: json.result.identityId,
            provider: 'cognito',
            sessionId: json.result.sessionId,
        });

        return json.result;
    } catch (ex) {
        logger.error('refreshSession', ex, {
            email,
            expiredAuthToken,
            preserveSessionId,
        });
        throw ex;
    }
}

function assertResponseHasNoErrors(response) {
    if (!response.error) {
        return;
    }

    const code = response.error.codeKey || response.error.code; // tix used error.code

    const error = new Error(code);

    error.code = code;
    error.responseBody = response;

    throw error;
}

function getHeaders(authToken = '', contentType = 'application/json') {
    const headers = {
        Accept: 'application/json, text/plain, */*',
    };

    if (authToken) {
        headers['X-TOKEN'] = authToken;
    }

    if (contentType) {
        headers['Content-Type'] = contentType;
    }

    return headers;
}

function isUnauthorized(response = {}) {
    return response.status === 401;
}

function hasAuthToken() {
    return !!cachedAuthToken;
}

function isFederatedSession() {
    const { provider } = cachedAuthCredentials;
    return !!provider && provider !== 'cognito';
}

// Recovers an expired session on a 401 and reports whether the original request
// should be retried. For federated sessions, defers to the injected account-layer
// re-auth (R3) — which mints a fresh session with a *fresh* provider token and
// clears credentials on definitive failure — instead of re-POSTing the dead
// cached provider token, and caps re-auth at exactly one attempt (R2/AC3).
// Cognito (refresh.json) and anonymous sessions keep the existing startNewSession
// recursion unchanged (no one-attempt cap).
async function recoverSession(opts) {
    if (isFederatedSession() && reauthenticate) {
        if (opts.sessionRecovered) {
            // Already re-authed once for this request — do not loop (AC3).
            return false;
        }
        // Concurrent 401s share the in-flight re-auth instead of each firing
        // their own SDK prompt / mint.
        if (reauthOperation) {
            await reauthOperation;
            return true;
        }
        try {
            // Throws on definitive/transient failure → propagates to the caller;
            // the 401 handler must not retry or re-POST in that case.
            reauthOperation = reauthenticate();
            await reauthOperation;
            return true;
        } finally {
            reauthOperation = null;
        }
    }

    await startNewSession();
    return true;
}

async function del(url, init = {}, opts = {}) {
    const { withAuth } = opts;

    try {
        if (withAuth && !hasAuthToken()) {
            await startNewSession();
        }

        const response = await fetch(url, {
            method: 'delete',
            ...init,
            headers: {
                ...getHeaders(withAuth
                    ? cachedAuthToken
                    : '', false),
                ...init.headers,
            },
        });

        if (isUnauthorized(response)) {
            const recovered = await recoverSession(opts);
            if (recovered) {
                return del(url, init, { ...opts, sessionRecovered: true });
            }
        }

        const json = await response.json();

        assertResponseHasNoErrors(json);

        return json;
    } catch (ex) {
        logger.warn('client delete error', ex, {
            init,
            resHeaders: ex?.response?.headers,
            url,
            withAuth,
        });
        throw ex;
    }
}

async function get(url, init = {}, opts = {}) {
    const { withAuth } = opts;

    try {
        if (withAuth && !hasAuthToken()) {
            await startNewSession();
        }

        const response = await fetch(url, {
            method: 'get',
            ...init,
            headers: {
                ...getHeaders(withAuth
                    ? cachedAuthToken
                    : '', false),
                ...init.headers,
            },
        });

        // Anonymous requests have no session to recover: retrying would loop
        // auth.json -> request -> 401 with no cap (#1185).
        if (withAuth && isUnauthorized(response)) {
            const recovered = await recoverSession(opts);
            if (recovered) {
                return get(url, init, { ...opts, sessionRecovered: true });
            }
        }

        const json = await response.json();

        assertResponseHasNoErrors(json);

        return json;
    } catch (ex) {
        // return the response headers for debugging
        logger.warn('client get error', ex, {
            init,
            resHeaders: ex?.response?.headers,
            url,
            withAuth,
        });
        throw ex;
    }
}

async function post(url, init = {}, opts = {}) {
    const {
        contentType,
        preserveSession,
        withAuth,
    } = opts;
    let { authViaHeader } = opts;

    try {
        if (withAuth && !hasAuthToken()) {
            await startNewSession();
        }

        if (withAuth && typeof init.body === 'string') {
            // Let's not get into a game of parsing the most likely json
            // and insert the X-TOKEN. So let's take the hit and do the OPTIONS calls
            authViaHeader = true;
        }

        if (withAuth && !authViaHeader) {
            /* eslint-disable-next-line no-param-reassign */
            init.body['X-TOKEN'] = cachedAuthToken;
        }

        const body = typeof init.body === 'string'
            ? JSON.parse(init.body)
            : init.body;

        if (url.includes('auth.json') && !body.meta && !Array.isArray(body)) {
            body.meta = getSessionMeta();
        }

        const response = await fetch(url, {
            method: 'post',
            ...init,
            body: JSON.stringify(body),
            headers: {
                ...getHeaders(withAuth && authViaHeader
                    ? cachedAuthToken
                    : '', contentType),
                ...init.headers,
            },
        });

        if (isUnauthorized(response) && !preserveSession) {
            const recovered = await recoverSession(opts);
            if (recovered) {
                return post(url, init, { ...opts, sessionRecovered: true });
            }
        }

        const json = await response.json();

        assertResponseHasNoErrors(json);

        return json;
    } catch (ex) {
        // return the post response headers for debugging
        logger.warn('client post error', ex, {
            init,
            resHeaders: ex?.response?.headers,
            url,
            withAuth,
        });
        throw ex;
    }
}

function clearCredentials() {
    cachedAuthCredentials = {
        accessToken: undefined,
        authToken: undefined,
        email: undefined,
        provider: undefined,
        sessionId: undefined,
        timestamp: undefined,
    };

    onSessionChangeListener(undefined);
}

function getSessionMeta() {
    const deviceIds = [];
    const userIds = [];

    const amplitudeDeviceId = getDeviceId();
    const amplitudeUserId = getUserId();

    if (amplitudeDeviceId) {
        deviceIds.push({
            id: amplitudeDeviceId,
            source: 'amplitude',
        });
    }
    if (amplitudeUserId) {
        userIds.push({
            id: amplitudeUserId,
            source: 'amplitude',
        });
    }

    return {
        affiliateId,
        deviceIds,
        spDomainSessionID: SnowplowService.getDomainUserInfo().domainSessionId,
        spDomainSessionIndex: SnowplowService.getDomainUserInfo().domainSessionIdx,
        spDomainUserID: SnowplowService.getDomainUserId(),
        spNetworkID: SnowplowService.getNetworkUserId(),
        userIds,
    };
}

async function startSession(authCredentials = cachedAuthCredentials) {
    const {
        accessToken,
        email,
        password,
        provider,
        timestamp,
    } = authCredentials;

    setAuthCredentials({
        accessToken,
        email,
        provider,
    });

    const payload = {
        meta: getSessionMeta(),
    };

    if (provider === 'cognito') {
        payload.credentials = {
            ...appSettings.config.wapi.credentials,
            email,
            password,
            type: 'email',
        };
    } else if (!!provider && provider !== 'cognito') {
        payload.credentials = {
            ...appSettings.config.wapi.credentials,
            provider: provider,
            token: accessToken,
            type: 'federated',
        };
    } else {
        payload.credentials = {
            ...appSettings.config.wapi.credentials,
            type: 'user',
            username: 'anonymous',
        };
    }

    try {
        const url = buildWapiUrl('auth.json');

        authOperation = fetch(url, {
            body: JSON.stringify(payload),
            headers: getHeaders('', 'text/plain'),
            method: 'post',
        });

        const response = await authOperation;
        const json = await response.json();

        assertResponseHasNoErrors(json);

        setSession({
            accessToken,
            authToken: json.result.token,
            email,
            provider,
            sessionId: json.result.sessionId,
            timestamp,
        });

        return json.result;
    } catch (ex) {
        // add more data here to get better reporting
        logger.error('startNewSession', ex, {
            meta: payload.meta,
            provider,
            type: payload.credentials.type,
        });
        throw ex;
    }
}

async function startNewSession(authCredentials = cachedAuthCredentials, options = {}) {
    const {
        accessToken,
        email,
        password,
        provider,
    } = authCredentials;
    const { preserveSessionId } = options;

    const timestamp = Date.now();

    if (authOperation && isSameAuthCreds(authCredentials, cachedAuthCredentials)) {
        /* only one call to auth.json with the same credentials at a time please */
        const result = await authOperation;
        return result;
    }

    if (provider === 'cognito'
        && email === cachedAuthCredentials.email
        && !password
        && cachedAuthToken
    ) {
        authOperation = refreshSession(email, cachedAuthToken, preserveSessionId);
    } else {
        authOperation = startSession({
            accessToken,
            email,
            password,
            provider,
            timestamp,
        });
    }

    try {
        const result = await authOperation;
        return result;
    } finally {
        authOperation = null;
    }
}

function buildWapiUrl(path) { // would have put this in wapiservice, but can't have circular references
    return appSettings.config.wapi.urlRoot + path;
}

function getSessionId() {
    return cachedSessionId;
}

function onSessionChange(listener) {
    onSessionChangeListener = listener;
}

function onReauthenticate(callback) {
    reauthenticate = callback;
}

export default {
    clearCredentials,
    delete: del,
    get,
    getSessionId,
    initService,
    onReauthenticate,
    onSessionChange,
    post,
    refreshSession,
    restoreSession,
    setSession,
    startNewSession,
};
